(() => {
  if (customElements.get('cabane-ranked-search')) return;
  const cache = new Map();
  const idList = value => (value || '').split(',').filter(id => /^\d+$/.test(id));
  const marketKey = (country, language, ids) => country + ':' + language + ':' + ids.slice().sort().join(',');
  const rankingQuery = 'query SalesOrder($q:String,$after:String,$country:CountryCode,$language:LanguageCode) @inContext(country:$country,language:$language) {products(first:250,after:$after,sortKey:BEST_SELLING,query:$q){nodes{id} pageInfo{hasNextPage endCursor}}}';
  async function rankedMembers(ids, country, language, signal) {
    const key = marketKey(country,language,ids);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < 300000) return hit.ids;
    // Scope comes from Shopify Online Store search, including every selected facet.
    // Directly filtering products by a catalog term can mean something different in Storefront GraphQL.
    const wanted = new Set(ids), order = [];
    const q = ids.length <= 250 ? ids.map(id => 'id:'+id).join(' OR ') : null;
    let after = null;
    do {
      const res = await fetch('/api/2026-10/graphql.json', {
        method:'POST', signal, credentials:'same-origin', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({query:rankingQuery,variables:{q,after,country,language}})
      });
      if (!res.ok) throw Error('Sales order unavailable');
      const result = await res.json(), connection = result.data?.products;
      if (result.errors?.length || !connection) throw Error('Invalid sales order');
      for (const product of connection.nodes) {
        const id = product.id.split('/').pop();
        if (wanted.delete(id)) order.push(id);
      }
      after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
    } while (after && wanted.size);
    // Keep the complete local market's set if a different channel has not indexed a product yet.
    order.push(...ids.filter(id => wanted.has(id)));
    cache.set(key,{ids:order,at:Date.now()});
    if (cache.size > 40) cache.delete(cache.keys().next().value);
    return order;
  }
  async function section(url, sectionId, signal) {
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id',sectionId);
    const response = await fetch(endpoint,{signal,credentials:'same-origin',headers:{Accept:'text/html'}});
    if (!response.ok) throw Error('Sales grid unavailable');
    return new DOMParser().parseFromString(await response.text(),'text/html');
  }
  async function members(url, signal) {
    const ids = [], index = new URL(url);
    index.searchParams.delete('page');
    let pages = 1;
    for (let page = 1; page <= pages; page++) {
      index.searchParams.set('page',String(page));
      const source = (await section(index,'cabane-sales-members',signal)).querySelector('[data-cabane-sales-members]');
      if (!source) throw Error('Missing filtered product index');
      pages = Number(source.dataset.pages);
      ids.push(...idList(source.dataset.ids));
    }
    return ids;
  }
  class RankedSearch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      // A soft navigation pushes its URL and inserts its content in the same frame.
      requestAnimationFrame(() => { if (!signal.aborted) this.update(signal).catch(() => {}); });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    async update(signal) {
      const url = new URL(location.href);
      const grid = this.parentElement?.querySelector('ul.product-grid');
      if (!grid || !/\/search\/?$/.test(url.pathname)) return;
      let ids = idList(this.dataset.ids);
      if (ids.length !== Number(this.dataset.count)) ids = await members(url, signal);
      if (!ids.length || signal.aborted) return;
      const order = ids.length === 1 ? ids : await rankedMembers(ids,this.dataset.country,this.dataset.language,signal);
      const size = Number(this.dataset.pageSize);
      const offset = (Number(this.dataset.page)-1)*size;
      const wanted = order.slice(offset,offset+size);
      const cards = new Map([...grid.children].map(card=>[card.dataset.cabaneProductId,card]));
      if (!wanted.length || signal.aborted) return;
      if (wanted.join(',') !== [...cards.keys()].join(',')) {
        if (wanted.some(id=>!cards.has(id))) {
          const endpoint = new URL(url);
          endpoint.search = new URLSearchParams({q:wanted.map(id=>'id:'+id).join(' OR '),type:'product'}).toString();
          if (url.searchParams.has('preview_theme_id')) endpoint.searchParams.set('preview_theme_id',url.searchParams.get('preview_theme_id'));
          const doc = await section(endpoint,this.dataset.section,signal);
          for (const card of doc.querySelectorAll('ul.product-grid > [data-cabane-product-id]')) cards.set(card.dataset.cabaneProductId,card);
        }
        if (!signal.aborted && grid.isConnected && wanted.every(id=>cards.has(id))) {
          const rows = wanted.map(id=>cards.get(id));
          // Shopify's reveal observer may already have scanned the initial grid.
          rows.forEach(row=>row.classList.remove('scroll-trigger--offscreen'));
          grid.replaceChildren(...rows);
        }
      }
      if (!signal.aborted) this.dataset.salesReady = 'true';
    }
  }
  customElements.define('cabane-ranked-search',RankedSearch);
})();
